// src/components/LiveStream/LiveStreamDetail.jsx
import React, { useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, Play, Lock } from "lucide-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useVideos } from "../../hooks/useVideos";

// ─── Subscription helper ───────────────────────────────────────────────────
const hasActiveSubscription = () => {
  try {
    const raw = localStorage.getItem("userSubscription");
    if (!raw) return false;
    const sub = JSON.parse(raw);
    if (sub?.status !== "Active") return false;
    if (!sub?.expiryDate) return false;
    return new Date(sub.expiryDate) > new Date();
  } catch {
    return false;
  }
};
// ──────────────────────────────────────────────────────────────────────────

const queryClient = new QueryClient();

const LiveStreamDetailWithProvider = () => (
  <QueryClientProvider client={queryClient}>
    <LiveStreamDetail />
  </QueryClientProvider>
);

const LiveStreamDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [isPlaying, setIsPlaying] = useState(false);

  // Try videos from router state first (instant), fallback to hook fetch
  const stateVideos = location.state?.videos || [];
  const { videos: fetchedVideos, loading } = useVideos({ fetchAll: true });

  // Merge: prefer state videos (fast), fill with fetched if needed
  const allVideos = stateVideos.length > 0 ? stateVideos : fetchedVideos;
  const video = allVideos.find((v) => v._id === id);

  // ── Helpers ──────────────────────────────────────────────────────────────
  const formatDateDMY = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB");
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  const getYouTubeThumbnail = (url) => {
    const vid = getYouTubeVideoId(url);
    return vid ? `https://img.youtube.com/vi/${vid}/maxresdefault.jpg` : null;
  };

  const getYouTubeEmbedUrl = (url) => {
    const vid = getYouTubeVideoId(url);
    return vid
      ? `https://www.youtube.com/embed/${vid}?autoplay=1&rel=0`
      : null;
  };

  // ── Subscription gate ────────────────────────────────────────────────────
  const isPreviewOnly = !hasActiveSubscription();

  const handlePlay = () => {
    if (isPreviewOnly) {
      navigate("/plan");
      return;
    }
    setIsPlaying(true);
  };

  const handleBack = () => navigate(-1);

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading && !video) {
    return (
      <div className="bg-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600" />
          <p className="mt-4 text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  // ── Not found ────────────────────────────────────────────────────────────
  if (!video) {
    return (
      <div className="bg-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-800 text-lg font-semibold mb-4">
            Video not found.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="px-5 py-2 rounded-full font-bold text-white bg-green-600 hover:bg-green-700 transition"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const thumbnailUrl = getYouTubeThumbnail(video.link);
  const embedUrl = getYouTubeEmbedUrl(video.link);
  const isToday =
    new Date(video.createdAt).toDateString() === new Date().toDateString();

  return (
    <div className="bg-white text-gray-900 min-h-screen">

      {/* ── Header ── */}
      <div className="bg-white border-b border-gray-200 flex items-center px-4 py-3 relative">
        <button
          onClick={handleBack}
          className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors z-10"
        >
          <ArrowLeft className="w-5 h-5 text-gray-700" />
        </button>
        <h1 className="absolute left-1/2 -translate-x-1/2 text-base font-bold text-gray-900 line-clamp-1 max-w-[60%] text-center">
          {video.title}
        </h1>
      </div>

      {/* ── Video / Thumbnail ── */}
      <div
        className="relative w-full bg-gray-100"
        style={{ paddingTop: "56.25%" }}
      >
        {isPlaying && embedUrl ? (
          <iframe
            className="absolute inset-0 w-full h-full"
            src={embedUrl}
            title={video.title}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <>
            {/* Thumbnail */}
            {thumbnailUrl ? (
              <img
                src={thumbnailUrl}
                alt={video.title}
                className="absolute inset-0 w-full h-full object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = "none";
                }}
              />
            ) : (
              <div className="absolute inset-0 bg-gray-200 flex items-center justify-center">
                <span className="text-gray-400 text-sm">No preview</span>
              </div>
            )}

            {/* Subtle dark overlay */}
            <div className="absolute inset-0 bg-black/25" />

            {/* Play / Lock button centered */}
            <button
              onClick={handlePlay}
              className="absolute inset-0 flex items-center justify-center group"
            >
          <div className="flex flex-col items-center gap-3">
  {/* Circle button */}
  <div className="w-16 h-16 bg-black/60 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/30 group-hover:scale-110 transition-transform duration-200">
    <Play className="w-7 h-7 text-white ml-1" fill="white" />
  </div>
</div>
            </button>

            {/* Time badge bottom-right */}
            <div className="absolute bottom-3 right-3 bg-black/70 backdrop-blur-sm px-2 py-1 rounded-lg text-xs text-white font-medium flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatTime(video.createdAt)}
            </div>
          </>
        )}
      </div>

      {/* ── Info Section ── */}
      <div className="px-5 pt-5 pb-5">

        {/* Title */}
        <h2 className="text-gray-900 text-2xl font-bold mb-3 leading-tight">
          {video.title}
        </h2>

        {/* Date + Status badge */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex items-center gap-1.5 text-gray-500 text-sm">
            <Calendar className="w-4 h-4 text-green-600" />
            <span>{isToday ? "Today" : formatDateDMY(video.createdAt)}</span>
          </div>
          <span className="bg-green-50 text-green-700 border border-green-200 text-xs font-bold px-3 py-1 rounded-md tracking-wide">
            {video.status?.toUpperCase() || "ACTIVE"}
          </span>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-100 mb-5" />

        {/* Description */}
        <div>
          <h3 className="text-gray-900 text-lg font-bold mb-2">Description</h3>
          <p className="text-gray-500 text-sm leading-relaxed">
            {video.description && video.description.trim() !== ""
              ? video.description
              : `LIVE Recording ${formatDateDMY(video.createdAt)}`}
          </p>
        </div>

      </div>
    </div>
  );
};

export default LiveStreamDetailWithProvider;